import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema, faqSchema, organizationSchema, SITE_URL, BUSINESS } from "@/lib/seo";
import { CONTACT, waHref, telHref, mailHref } from "@/lib/contact";
import { trackEvent } from "@/lib/analytics";
import { Phone, MessageCircle, Mail, ArrowRight, MapPin } from "lucide-react";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: `${SITE_URL}/contact`,
  about: {
    "@type": "Organization",
    name: BUSINESS.brand,
    email: BUSINESS.email,
    telephone: BUSINESS.phone,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      contactType: "customer service",
      areaServed: "AE",
      availableLanguage: ["English", "Arabic"],
    },
  },
};

const CONTACT_FAQS = [
  { q: "How quickly will I hear back?", a: "We reply as soon as possible during working hours. Share your property type, community, approximate size and preferred date and we'll respond with an exact, no-obligation quote and confirmed availability." },
  { q: "Which areas do you cover?", a: "Dubai is our primary market. Abu Dhabi and Sharjah are served subject to confirmed availability — mention your community and we confirm coverage with your quote." },
  { q: "How fast is the inspection report?", a: "The reporting timeframe is confirmed in the accepted quotation. An eight-hour reporting service may be available for eligible single-property bookings when expressly confirmed in writing. Partner bookings, occupied properties, restricted-access inspections, commercial work and multi-property assignments follow the agreed reporting programme." },
  { q: "What details should I include in my request?", a: "The service you need, property type, community, approximate size, preferred date and whether access is arranged. That is enough for an exact fixed quote." },
  { q: "Can I talk to an inspector directly?", a: "Yes — call +971 4 296 3910, email info@fedari.ae or message the Fedari team on WhatsApp. You speak directly with the team, before and after the inspection." },
];

export default function Contact() {
  const path = "/contact";
  return (
    <PageShell breadcrumbs={[{ name: "Contact", path }]}>
      {({ onBook, onQuote }) => (
        <>
          <Seo
            title="Contact Fedari Inspection | Book a UAE Property Inspection"
            description="Contact Fedari Inspection for a property snagging or handover inspection in Dubai (and, subject to availability, Abu Dhabi & Sharjah). Call, WhatsApp or email for a fast quote."
            path={path}
            jsonLd={[contactSchema, organizationSchema(), faqSchema(CONTACT_FAQS), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path }])]}
          />
          <section className="wrap py-10 lg:py-14">
            <span className="eyebrow">Get in touch</span>
            <h1 className="text-[clamp(2rem,4vw,3rem)] tracking-[-0.03em] mt-3 mb-4">Contact Fedari Inspection</h1>
            <p className="text-[1.1rem] text-bodytext max-w-[60ch] mb-9">
              Tell us about your property and we&apos;ll confirm an exact, no-obligation quote as soon as possible.
              Prefer to talk? Call us or message a real inspector on WhatsApp.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-[90ch] mb-10">
              <a href={telHref} onClick={() => trackEvent("phone_clicked")} data-testid="contact-phone"
                className="border border-line rounded-xl p-5 hover:border-blue transition-colors">
                <span className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center text-blue mb-3"><Phone size={18} /></span>
                <div className="font-mono-jb text-[0.66rem] tracking-wider text-muted uppercase">Phone</div>
                <div className="font-semibold text-ink">{CONTACT.phoneDisplay}</div>
              </a>
              <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_clicked")} data-testid="contact-whatsapp"
                className="border border-line rounded-xl p-5 hover:border-wa transition-colors">
                <span className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center text-wa mb-3"><MessageCircle size={18} /></span>
                <div className="font-mono-jb text-[0.66rem] tracking-wider text-muted uppercase">WhatsApp</div>
                <div className="font-semibold text-ink">Message an inspector</div>
              </a>
              <a href={mailHref} data-testid="contact-email" onClick={() => trackEvent("click_email", { location: "contact" })}
                className="border border-line rounded-xl p-5 hover:border-blue transition-colors">
                <span className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center text-blue mb-3"><Mail size={18} /></span>
                <div className="font-mono-jb text-[0.66rem] tracking-wider text-muted uppercase">Email</div>
                <div className="font-semibold text-ink">{CONTACT.email}</div>
              </a>
              <div className="border border-line rounded-xl p-5" data-testid="contact-office">
                <span className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center text-blue mb-3"><MapPin size={18} /></span>
                <div className="font-mono-jb text-[0.66rem] tracking-wider text-muted uppercase">Office</div>
                <address className="not-italic text-[0.85rem] text-bodytext leading-relaxed">
                  {CONTACT.addressLines.map((l) => <div key={l}>{l}</div>)}
                </address>
                <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-blue text-[0.82rem] font-semibold hover:underline" data-testid="contact-maps-link">
                  Open location in Google Maps
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={onQuote} data-testid="contact-quote-btn" className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">Get quote &amp; availability <ArrowRight size={16} /></button>
              <a href={telHref} onClick={() => trackEvent("click_phone", { location: "contact" })} data-testid="contact-call-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold hover:border-ink transition-colors"><Phone size={16} /> Call {CONTACT.phoneDisplay}</a>
            </div>

            <p className="text-sm text-muted mt-10 pt-6 border-t border-line max-w-[70ch]">
              FEDARI BUILDING INSPECTION L.L.C · UAE Trade Licence 1647070 · {CONTACT.addressLines.join(", ")} · {CONTACT.addressNote} Serving Dubai, Abu Dhabi, Sharjah and
              the wider United Arab Emirates.
            </p>
          </section>

          {/* Contact FAQ */}
          <section className="wrap pb-16" data-testid="contact-faq">
            <h2 className="text-2xl mb-5">Contact — frequently asked questions</h2>
            <div className="space-y-4 max-w-[80ch]">
              {CONTACT_FAQS.map((f, i) => (
                <div key={i} className="border border-line rounded-xl p-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-bodytext text-[0.95rem]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
